import React, { useMemo } from "react";
import { Empty, Table, Typography } from "antd";
import useIsMobile from "../../../../hooks/useIsMobile";
import dayjs from "dayjs";
import { careTypeLabel } from "../../../IpEMR/Options/careItemsOptions";

const { Text } = Typography;

/**
 * Vitals, GRBS and nebulisation are already presented as their own tables on
 * this form, so they are left out of the care items list rather than repeated.
 * Matched on `care_type__code`, which is the stable key — `care_type__name`
 * is the display label ("GRBS Monitoring") and can be reworded.
 *
 * Specialist consultation used to be excluded here as well. It has no table
 * of its own on this form, so excluding it meant the consultations ordered
 * for the patient did not appear anywhere on the shift form - which is the
 * one thing a shift form is for. It lists with the rest.
 */
export const EXCLUDED_CARE_TYPES = [
  "vitals",
  "grbs",
  "grbs_monitoring",
  "nebulization",
  "nebulisation",
];

export const isExcludedCareType = (item) => {
  const code = item?.care_type__code;
  if (code) return EXCLUDED_CARE_TYPES.includes(code);
  // Older records may carry only the name; fall back to a slug of it.
  const slug = String(item?.care_type__name || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
  return EXCLUDED_CARE_TYPES.includes(slug);
};

/** "medication_amount" -> "Medication Amount" */
const prettyKey = (key) =>
  String(key || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * Values are stored as slugs ("4_hourly", "rl", "sdp"). Short all-letter
 * tokens are abbreviations and read better upper-cased; longer ones are
 * words and get title case.
 */
const prettyValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (Array.isArray(value) || typeof value === "object") {
    const raw = Array.isArray(value) ? value : Object.values(value);
    const parts = raw.map(prettyValue).filter(Boolean);
    return parts.length ? parts.join(", ") : null;
  }
  return String(value)
    .split("_")
    .filter(Boolean)
    .map((token) =>
      /^[a-z]{1,3}$/.test(token)
        ? token.toUpperCase()
        : token.charAt(0).toUpperCase() + token.slice(1)
    )
    .join(" ");
};

/**
 * Flatten a care item's free-form `data` into label/value pairs. A key that
 * just repeats the care type ({ vitals: "4_hourly" }) carries no extra
 * information as a label, so only its value is shown.
 */
const isOtherChoice = (raw) =>
  typeof raw === "string" && /^others?$/i.test(raw.trim());

/*
 * Where a field was answered "Others", what was actually typed.
 *
 * The pair is stored as two keys - `medication: "others"` beside
 * `other_medication: "Adrenaline"`, `fluids: "others"` beside `other_fluid`
 * (singular, where the field is plural). Shown as they are stored that reads
 * "Medication Others" and then "Other Medication Adrenaline", which says the
 * same thing twice and buries the answer in the second half. The typed text
 * is used as the field's own value and its key is not shown again.
 */
const otherTextKeyFor = (key, data) => {
  const singular = key.replace(/s$/, "");
  const candidates = [
    `other_${key}`,
    `other_${singular}`,
    `others_${key}`,
    `${key}_other`,
    `${key}_others`,
    `${key}_other_name`,
    `${key}_others_name`,
  ];
  return candidates.find(
    (candidate) =>
      candidate !== key &&
      Object.prototype.hasOwnProperty.call(data, candidate) &&
      prettyValue(data[candidate])
  );
};

const toPairs = (data, careTypeCode) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return [];

  /* Keys folded into the field they belong to, so they do not list twice. */
  const absorbed = new Set();
  const otherTextFor = {};
  Object.entries(data).forEach(([key, raw]) => {
    if (!isOtherChoice(raw)) return;
    const textKey = otherTextKeyFor(key, data);
    if (!textKey) return;
    otherTextFor[key] = data[textKey];
    absorbed.add(textKey);
  });

  return Object.entries(data)
    .map(([key, raw]) => {
      if (absorbed.has(key)) return null;
      const source = key in otherTextFor ? otherTextFor[key] : raw;
      const value = prettyValue(source);
      if (!value) return null;
      const repeatsType = key === careTypeCode;
      return { key, label: repeatsType ? null : prettyKey(key), value };
    })
    .filter(Boolean);
};

/**
 * `careItems` is the admission's ordered care, in the shape the form stores:
 * [{ care_type__code, care_type__name, data, order_date }].
 */
const EmergencyCareItems = ({ careItems }) => {
  const isMobile = useIsMobile();
  const rows = useMemo(() => {
    if (!Array.isArray(careItems)) return [];
    return careItems.filter((item) => !isExcludedCareType(item));
  }, [careItems]);

  if (!rows.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No care items ordered"
      />
    );
  }

  const rowKeyOf = (record, index) =>
    `${record?.care_type__code || record?.care_type__name || "care"}-${
      record?.order_date || index
    }`;

  const renderDetails = (value, record) => {
    const pairs = toPairs(value, record?.care_type__code);
    if (!pairs.length) return null;
    return (
      <div className="emergency-care-details">
        {pairs.map((pair) => (
          <div className="emergency-care-detail" key={pair.key}>
            {pair.label && (
              <Text className="emergency-care-detail-label">{pair.label}</Text>
            )}
            <Text className="emergency-care-detail-value">{pair.value}</Text>
          </div>
        ))}
      </div>
    );
  };

  const formatOrderedOn = (value) =>
    value ? dayjs(value).format("DD MMM YYYY, hh:mm A") : "-";

  /* A phone has nowhere near the ~640px the three columns need to stay
     readable, and a table squeezed into that width leaves the details column
     sliced off past the right edge with the sideways scroll easy to miss. So
     the same three fields are stacked into a card per item instead, each
     carrying its own label in place of the dropped column headings. */
  if (isMobile) {
    return (
      <div className="emergency-care-list">
        {rows.map((record, index) => (
          <div className="emergency-care-card" key={rowKeyOf(record, index)}>
            <Text strong className="emergency-care-card-title">
              {careTypeLabel(record?.care_type__code, record?.care_type__name)}
            </Text>
            <div className="emergency-care-card-date">
              Ordered on {formatOrderedOn(record?.order_date)}
            </div>
            {renderDetails(record?.data, record)}
          </div>
        ))}
      </div>
    );
  }

  const columns = [
    {
      title: "Care Item",
      dataIndex: "care_type__name",
      key: "care_type__name",
      width: "24%",
      /* Resolved from the code so sibling items read alike - see
         careTypeLabel. The server's own name is the fallback. */
      render: (value, record) => (
        <Text strong>{careTypeLabel(record?.care_type__code, value)}</Text>
      ),
    },
    {
      title: "Ordered On",
      dataIndex: "order_date",
      key: "order_date",
      width: "20%",
      render: formatOrderedOn,
    },
    {
      title: "Details",
      dataIndex: "data",
      key: "data",
      render: (value, record) => renderDetails(value, record) || "-",
    },
  ];

  return (
    <Table
      dataSource={rows}
      rowKey={rowKeyOf}
      columns={columns}
      pagination={false}
      className="emergency-care-items"
      scroll={{ x: "640px" }}
    />
  );
};

export default EmergencyCareItems;
